import React, { type ComponentType } from 'react';

import { type IntlShape } from 'react-intl-next';
import Loadable from 'react-loadable';

import { DefaultExtensionProvider, type ExtensionParams } from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { DEFAULT_GRAY_COLOR } from '@atlassian/color-picker';
import {
	Alignment,
	CornerStyle,
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	Orientation,
	type ProductDependenciesProviderProps,
	RESOURCE_TILES_EXTENSION_TYPE,
	Height as Size,
} from '@atlassian/company-hub-common';

import { RESOURCE_TILES_EXTENSION_KEY, RESOURCE_TILES_EXTENSION_VERSION_1 } from '../../constants';
import { type ResourceTilesParameters } from '../../types';

import { i18n } from './messages';

const ResourceTilesExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_ResourceTilesExtension" */ '../resource-tiles-extension'
			)
		).ResourceTilesExtension,
	loading: () => <UFOLoadHold name="resource-tiles-extension-loader"></UFOLoadHold>,
});

export const getInitialResourceTilesParametersV1 = (intl?: IntlShape): ResourceTilesParameters => ({
	version: RESOURCE_TILES_EXTENSION_VERSION_1,
	extensionTitle: intl?.formatMessage(i18n.title) || '',
	orientation: Orientation.STACKED,
	isPaddingEnabled: true,
	cornerStyle: CornerStyle.ROUNDED,
	backgroundColor: token('elevation.surface.overlay'),
	textColor: token('color.text'),
	borderColor: DEFAULT_GRAY_COLOR,
	alignment: Alignment.CENTER,
	size: Size.MEDIUM,
	isAutoscrollEnabled: false,
	imageLastUpdated: Date.now(),
	// Should be empty since it will be populated by API call
	cards: [],
});

export type ResourceTilesExtensionProviderWrapperProps = {
	contentId: string;
	isInEditor: boolean;
	propsProvider: ComponentType<ProductDependenciesProviderProps>;
};

export const resourceTilesExtensionProvider = async ({
	contentId,
	propsProvider,
	isInEditor,
}: ResourceTilesExtensionProviderWrapperProps) => {
	return async ({ editorActions, openConfigPanel, intl }: CustomSitesExtensionProviderProps) => {
		const title = intl.formatMessage(i18n.title);
		const description = intl.formatMessage(i18n.description);
		const type = RESOURCE_TILES_EXTENSION_TYPE;
		const render = async () => {
			return ({ node }: { node: ExtensionParams<ResourceTilesParameters> }) => (
				<ResourceTilesExtension
					contentId={contentId}
					isInEditor={isInEditor}
					propsProvider={propsProvider}
					extensionNode={node}
				/>
			);
		};
		const icons = {
			// ADD correct icon. this is a placeholder for now
			// eslint-disable-next-line import/dynamic-import-chunkname
			'48': () =>
				import(
					/* webpackChunkName: "@atlaskit-internal_ResourceTilesExtensionIcon" */
					'./resource-tiles-extension-icon/index'
				).then((mod) => mod.ResourceTilesExtensionIcon),
		};

		const parameters = getInitialResourceTilesParametersV1(intl);

		const update = async () => {
			const selectedNode = editorActions?.getSelectedNode()?.toJSON();
			const selectedLocalId = selectedNode?.attrs?.localId;

			if (!!selectedLocalId && openConfigPanel) {
				openConfigPanel(selectedLocalId);
			}
		};

		return new DefaultExtensionProvider([
			getExtensionManifest<ResourceTilesParameters>({
				key: RESOURCE_TILES_EXTENSION_KEY,
				icons,
				parameters,
				render,
				update,
				title,
				type,
				description,
				source: undefined,
			}),
		]);
	};
};
